import { useState, useRef, useEffect, forwardRef } from 'react';
import type { ChangeEvent, PropsWithoutRef, KeyboardEvent as ReactKeyboardEvent } from 'react';
import styled, { css } from 'styled-components';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  defaultThemeProp,
  Flex,
  getFocusables,
  Icon,
  registerIcon,
  StyledCard,
  Switch,
  Text,
  Progress,
  usePrevious,
  useLiveLog,
  useItemIntersection,
  useUID,
  useConsolidatedRef,
  type RefElement
} from '@pega/cosmos-react-core';
import { useArrows, useElement, useI18n, useOuterEvent } from '@pega/cosmos-react-core/lib/hooks';
import * as filterIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/filter.icon';
import * as filterOnIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/filter-on.icon';
import { StyledCardHeader } from '@pega/cosmos-react-core/lib/components/Card/CardHeader';
import Dialog from '@pega/cosmos-react-core/lib/components/Dialog/Dialog';

import { FeedContext } from './Feed.context';
import FeedButton from './FeedButton';
import type { FeedProps } from './Feed.types';

registerIcon(filterIcon, filterOnIcon);

const StyledFeed = styled.div`
  ${StyledCardHeader}:not(${StyledCard} ${StyledCard} > ${StyledCardHeader}) {
    padding-bottom: 0.25rem;
  }
`;

StyledFeed.defaultProps = defaultThemeProp;

export const StyledFilterDialog = styled(Dialog)(({ theme }) => {
  return css`
    min-width: fit-content;
    fieldset {
      padding-block-end: calc(2 * ${theme.base.spacing});
      border: none;
      max-height: inherit;

      legend {
        padding-block-start: calc(2 * ${theme.base.spacing});
        padding-inline: calc(2 * ${theme.base.spacing});
      }
    }
  `;
});

StyledFilterDialog.defaultProps = defaultThemeProp;

const StyledFilterList = styled.ul(({ theme }) => {
  return css`
    list-style-type: none;
    min-width: ${theme.base['content-width'].xs};
    padding-inline: calc(2 * ${theme.base.spacing});
    overflow: auto;
  `;
});

StyledFilterList.defaultProps = defaultThemeProp;

const StyledLoadMore = styled.div(({ theme }) => {
  return css`
    margin-top: ${theme.base.spacing};
    min-height: 2rem;
    min-width: 2rem;
  `;
});

StyledLoadMore.defaultProps = defaultThemeProp;

const Feed = forwardRef<RefElement<FeedProps>, PropsWithoutRef<FeedProps>>(
  function Feed(props, ref) {
    const {
      loadingPosts = false,
      progress = false,
      children,
      userInfo,
      className,
      title,
      searchTypes,
      onDecoratorSearch,
      searchResults = [],
      autopilotInfo = {
        id: 'autopilot',
        label: 'Autopilot'
      },
      onAutopilotClick,
      onMentionClick,
      onMentionPreview,
      onTagClick,
      interactionRenderers = [],
      markdownMap,
      filters,
      onFilterChange,
      onLoadMore,
      onLinkRender,
      newPostRegion,
      posts = [],
      ...restProps
    } = props;
    const t = useI18n();
    const dialogRef = useRef<HTMLDivElement>(null);
    const [buttonEl, setButtonEl] = useElement<HTMLButtonElement>();
    const headingId = useUID();
    const dialogId = useUID();
    const [mountDialog, setMountDialog] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const prevPosts = usePrevious(posts);
    const { announcePolite } = useLiveLog();

    const feedRef = useConsolidatedRef(ref);

    const filterRef = useRef(null);
    useArrows(filterRef);

    useOuterEvent('mousedown', [dialogRef, buttonEl], () => {
      setMountDialog(false);
    });

    useOuterEvent('focusin', [dialogRef, buttonEl], () => {
      setMountDialog(false);
    });

    const onKeydown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') setMountDialog(false);
    };

    useEffect(() => {
      document.addEventListener('keydown', onKeydown);
      return () => {
        document.removeEventListener('keydown', onKeydown);
      };
    }, []);

    const id = useUID();
    useItemIntersection(
      { current: null },
      posts.length - 1,
      () => {
        onLoadMore?.();
      },
      `#${id} > article[role='none']`,
      feedRef
    );

    useEffect(() => {
      if (loadingPosts) {
        announcePolite({ message: t('loading_post') });
      }
    }, [loadingPosts]);

    useEffect(() => {
      if (prevPosts) {
        const difference = posts.length - prevPosts.length;
        if (difference < 0) announcePolite({ message: t('feed_post_removed') });
        else if (difference > 0) {
          announcePolite({
            message: t('feed_new_post_added', [difference], { count: difference })
          });
        }
      }
    }, [posts]);

    useEffect(() => {
      if (mountDialog) {
        setTimeout(() => {
          const focusables = getFocusables(filterRef);
          focusables[0]?.focus();
        }, 0);
      }
    }, [mountDialog]);

    return (
      <FeedContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          searchTypes,
          onSearch: onDecoratorSearch,
          searchResults,
          showSearchResults,
          setShowSearchResults,
          autopilotInfo,
          onLinkRender,
          onAutopilotClick,
          onMentionClick,
          onMentionPreview,
          onTagClick,
          interactionRenderers,
          markdownMap,
          userInfo,
          feedRef
        }}
      >
        <StyledFeed ref={feedRef} {...restProps}>
          <Card>
            <CardHeader>
              <Text variant='h2'>{title}</Text>
              {filters && filters.length > 0 && (
                <Button
                  as={FeedButton}
                  ref={setButtonEl}
                  variant='simple'
                  icon
                  onClick={() => setMountDialog(curr => !curr)}
                  label={t('filters')}
                  aria-label={`${t('filters')} - ${t('pulse')}`}
                  aria-expanded={!!filters && mountDialog}
                  aria-owns={!!filters && mountDialog && buttonEl ? dialogId : undefined}
                >
                  <Icon
                    name={
                      filters?.map(filter => !!filter.on).reduce((acc, curr) => acc || curr)
                        ? 'filter-on'
                        : 'filter'
                    }
                  />
                </Button>
              )}

              {!!filters && mountDialog && buttonEl && (
                <StyledFilterDialog
                  ref={dialogRef}
                  target={buttonEl}
                  aria-labelledby={headingId}
                  id={dialogId}
                  onKeyDown={(event: ReactKeyboardEvent) => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      buttonEl?.focus();
                    }
                  }}
                >
                  <Flex container={{ direction: 'column', gap: 2 }} as='fieldset'>
                    <Text variant='h3' as='legend' id={headingId}>
                      {t('filters')}
                    </Text>
                    <StyledFilterList ref={filterRef}>
                      {filters?.map(filter => {
                        return (
                          <li key={filter.id}>
                            <Switch
                              {...filter}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                onFilterChange?.(event.target.id, event.target.checked)
                              }
                            />
                          </li>
                        );
                      })}
                    </StyledFilterList>
                  </Flex>
                </StyledFilterDialog>
              )}
            </CardHeader>

            <CardContent>
              {progress ? <Progress message={t('loading')} placement='block' /> : newPostRegion}
            </CardContent>
          </Card>

          <div id={id}>{posts}</div>

          {loadingPosts && (
            <Flex container={{ justify: 'center' }} as={StyledLoadMore}>
              <Progress placement='inline' />
            </Flex>
          )}
        </StyledFeed>
      </FeedContext.Provider>
    );
  }
);

export default Feed;
